<template>
    <div>

        <div v-if="loading" class="text-center">
            <i class="fa fa-circle-o-notch fa-spin"></i> Loading
        </div>
        <div v-else>
            <vue-headful :title="(hse.hse_id ? 'Edit MIC: ' + hse.hse_type_name : 'New MIC') + ' :: MeritEHS'"/>

            <h4 class="py-3 mb-4">
                <ol class="breadcrumb font-weight-bold m-0">
                    <li class="breadcrumb-item font-weight-light">
                        <router-link :to="`/hse`">
                            <i class="fa fa-shield"></i> Management Involvement & Commitment
                        </router-link>
                    </li>
                    <template v-if="hse.hse_id">
                        <li class="breadcrumb-item font-weight-light">
                            <router-link :to="`/hse/view/${hse.hse_id}`">
                                {{ hse.hse_type_name }} on {{ hse.hse_date_formatted }} for {{ hse.FirstName + ' ' + hse.LastName  }}
                            </router-link>
                        </li>
                        <li class="breadcrumb-item active">Edit</li>
                    </template>
                    <template v-else>
                        <li class="breadcrumb-item active">New MIC</li>
                    </template>
                    <li class="ml-3">
                        <button type="button" class="btn btn-sm btn-success" v-on:click="saveMIC(this)" :disabled="saving">
                            <span v-if="hse.hse_id">Update</span>
                            <span v-else>Create</span>
                            MIC
                        </button>
                    </li>
                </ol>
            </h4>

            <div class="row">
                <div class="col-xl-8 col-lg-10 col-md-12">
                    <div v-if="missing_employee" class="card mb-3">
                        <div class="card-header">
                            Missing Employee Record
                        </div>
                        <div class="card-body">
                            <div class="alert alert-warning">
                                <p>Your user record does not have an associated Employee record, so you cannot create your own MIC records. Contact an account administrator to link your employee record to your user record.</p>

                                <template v-if="admins.length > 0">
                                    <strong>Account Administrators:</strong>
                                    <ul>
                                        <li v-for="admin in admins">
                                            {{ admin.Firstname }} {{ admin.LastName }} - <a :href="`mailto:${admin.email}`" :title="`Click to Send an Email to ${admin.FirstName}`">{{ admin.email }}</a>
                                        </li>
                                    </ul>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div v-else class="card mb-3">
                        <div class="card-header"><template v-if="hse.hse_id">Edit</template><template v-else>Create</template> MIC Participation</div>
                        <div class="card-body">

                            <div v-if="this.$root.hasPermission('hse.manage')" class="form-group row">
                                <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.employee_id}">
                                    Employee: <i class="fa fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-sm-8">
                                    <employee-select :selectedValues="empSelectValues" @vselected="onEmpSelect" :hasError="errors.employee_id ? true : false"></employee-select>
                                    <span v-if="errors.employee_id" class="text-danger">
                                        <span v-for="error in errors.employee_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="form-group row">
                                <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.hse_type_id}">
                                    MIC Type: <i class="fa fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-sm-8">
                                    <select v-model="hse.hse_type_id" :class="{'form-control': true, 'is-invalid': errors.hse_type_id}">
                                        <option :value="null">-- Select a Type --</option>
                                        <option v-for="(value, key) in types" v-bind:value="key">
                                            {{ value }}
                                        </option>
                                    </select>
                                    <span v-if="errors.hse_type_id" class="text-danger">
                                        <span v-for="error in errors.hse_type_id">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.JobsiteId }" class="row">
                                <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.JobsiteId}">Jobsite Name:</label>
                                <div class="col-sm-8">
                                    <jobsite-select :selectedValues="SiteSelectedValues" v-on:vselected="onJobSiteSelectUpdate" :hasError="errors.JobsiteId ? true : false"></jobsite-select>
                                    <span v-if="errors.JobsiteId" class="text-danger">
                                        <span v-for="error in errors.JobsiteId">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div class="row form-group">
                                <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.division_id}">
                                    Division: <i class="fa fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-sm-8">
                                    <b-form-select v-model="hse.division_id" :options="divisionOptions" :class="{'is-invalid': errors.division_id}"></b-form-select>

                                    <div v-if="errors.division_id" class="text-danger">
                                        <span v-for="error in errors.division_id">{{ error }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.hse_date }" class="row">
                                <label class="col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right">
                                    Date:  <i class="fa fa-asterisk text-danger"></i>
                                </label>
                                <div class="col-sm-8">
                                    <date-picker v-model="hse.hse_date" @dp-change="datePickerChanged" :config="options" ></date-picker>
                                    <span v-if="errors.hse_date" class="text-danger">
                                        <span v-for="error in errors.hse_date">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                            <div v-bind:class="{ 'form-group': true, 'has-error': errors.files }" class="row">
                                <label class="col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right">Files:</label>
                                <div class="col-sm-8">

                                    <b-form-file v-model="files" class="mt-3" plain multiple></b-form-file>

                                    <span v-if="errors.files" class="text-danger">
                                        <span v-for="error in errors.files">{{ error }}</span>
                                    </span>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
            <hr/>
            <button type="button" class="btn btn-success" v-on:click="saveMIC(this)" :disabled="saving" v-if="!missing_employee">
                <span v-if="hse.hse_id">Update</span>
                <span v-else>Create</span>
                MIC
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                loading: true,
                saving: false,
                hse_id: this.$route.params.id,
                hse: {},
                files: [],
                types: {},
                divisions: [],
                admins: [],
                errors: {},
                options: {
                    format: 'MM/DD/YYYY',
                    date: ""
                },
                datePicked: new Date(),
                missing_employee: false,
            }
        },

        computed: {
            //User Setter
            empSelectValues(){
                //Check for null
                if(!this.hse.employee_id || this.hse.employee_id == null)
                {
                    return null;
                }

                //Send it
                return { EmployeeId: this.hse.employee_id, FirstName: this.hse.FirstName, LastName: this.hse.LastName, EmployeeNumber: this.hse.EmployeeNumber};
            },

            SiteSelectedValues(){
                //check for null
                if(!this.hse.JobsiteId || this.hse.JobsiteId == null)
                {
                    //Check the store
                    if(this.$store.state.user.jobsite)
                    {
                        //Set the jobsite
                        var jobsite = this.$store.state.user.jobsite;

                        //Set the MIC jobsite
                        this.hse.JobsiteId = jobsite.JobsiteId;

                        //Send it
                        return {JobsiteId: jobsite.JobsiteId, JobsiteNumber: jobsite.JobsiteNumber, JobsiteName: jobsite.JobsiteName};
                    }

                    //Send it
                    return null;
                }

                //Send it
                return {JobsiteId: this.hse.JobsiteId, JobsiteNumber: this.hse.JobsiteNumber, JobsiteName: this.hse.JobsiteName};
            },

            //Build it
            divisionOptions(){
                //Setup options
                var options = [
                    {value: null, text: '-- Select a Division--'},
                ];

                //Loop through and build lists
                for(var i = 0; i < this.divisions.length; i++)
                {
                    //Push onto options
                    options.push({value: this.divisions[i].DivisionId, text: (this.divisions[i].division_code+ ' - '+this.divisions[i].DivisionName)});
                }

                //Send it
                return options;
            }
        },

        methods: {
            //Get the Various Resources We Need to Edit a Jobsite
            getErrorClass: function(attribute) {
                return {
                    'form-group has-error': this.errors[attribute],
                    'form-group': !this.errors[attribute],
                }
            },

            getMIC: function() {
                //Get the data
                this.$http
                    .get('hse/edit' + (this.hse_id ? '/' + this.hse_id : ''))
                    .then(response => {
                        //Set all the data
                        this.missing_employee = response.data.missing_employee;
                        this.admins = response.data.admins;

                        //Set other stuff
                        this.hse = response.data.hse;
                        this.types = response.data.types;
                        this.divisions = response.data.divisions;
                        this.options.date = this.hse.hse_date;

                        //Setters
                        if(!this.hse_id)
                        {
                            this.hse.division_id = null;
                        }
                    })
                    .catch(response => {

                    })
                    .then(() => {
                        //Set loading
                        this.loading = false;
                    });
            },

            saveMIC: function() {
                //SHow loading
                this.saving = true;

                //Reset the errors
                this.errors = {};

                //Send it
                this.$http
                .postWithFiles('hse/edit' + (this.hse_id ? '/' + this.hse_id : ''), this.hse, this.files)
                .then(response => {
                    //Alert the user?
                    this.$toastr.success('MIC Saved');

                    //Return to main list?
                    this.$router.push('/hse/view/' + response.data.hse_id);
                })
                .catch(error => {
                    //Check for error
                    if(error.response.status === 422)
                    {
                        //Display validation errors
                        this.errors = error.response.data
                    }
                })
                .then(response => {
                    //SHow loading
                    this.saving = false;
                });
            },

            onJobSiteSelectUpdate (jobsite) {
              //Check for null
                if(jobsite == null)
                {
                    //Stop
                    this.hse.JobsiteId = null;
                    return;
                }

                //Send the value
                this.hse.JobsiteId = jobsite.JobsiteId;
                this.hse.division_id = jobsite.DivisionId;
                //this.hse.JobsiteName = jobsite.JobsiteName;
                //this.hse.JobsiteNumber = jobsite.JobsiteNumber;
            },

            onEmpSelect (employee)
            {
                //Check for null
                if(employee == null)
                {
                    this.hse.employee_id = null
                    return;
                }

                //Set it
                this.hse.employee_id = employee.EmployeeId;
            },

            datePickerChanged ( data ) {
                // Stringify datePicked
                var dateParts = data.target.value.split("/");
                this.hse.hse_date = dateParts[2]+'-'+dateParts[0]+'-'+dateParts[1];
            },
        },

        mounted() {
            //Get the MIC
            this.getMIC();
        }
    }
</script>
